import { ORIGIN_COOKIE } from 'components/Payment/constants'
import { VenuePageLoader } from 'components/Venue/VenuePageLoader'
import { useLogger } from 'hooks/useLogger'
import Cookies from 'js-cookie'
import { useEonxCheckInGuestMutation } from 'lib/gql'
import { observer } from 'mobx-react-lite'
import Router, { useRouter } from 'next/router'
import qs from 'query-string'
import React, { FC, ReactNode, useCallback, useState } from 'react'
import { useDebounce, useEffectOnce } from 'react-use'

import { updateEonxEnvironment } from './utils'

type Props = {
  venueSlug: string | null
  children: ReactNode
}

const VENUE_COLLECTION_PATHNAME = '/[venueSlug]/c/[vcslug]'

export const EonxListener: FC<Props> = observer(({ venueSlug, children }) => {
  const { query, pathname } = useRouter()
  /*
    The Venue Collection page may have the eonxCheckInId query param attached but we dont want to check-in here
    as we don't have a venue selected yet. We can likely disable this when this ticket is completed:
    https://linear.app/mr-yum/issue/SRV-1329/redirect-from-venue-collection-page-to-avoid-sending-data-center
  */
  const enableCheckIn: boolean =
    pathname !== VENUE_COLLECTION_PATHNAME &&
    !!query.eonxCheckInId &&
    !!venueSlug
  const [loading, setLoading] = useState<boolean>(enableCheckIn ? true : false)
  const [, setError] = useState<boolean>(false)
  const { sendLog } = useLogger()
  const [, eonxCheckInGuest] = useEonxCheckInGuestMutation()

  const onSuccess = useCallback(async () => {
    Cookies.set(ORIGIN_COOKIE, 'eonx')
    updateEonxEnvironment()

    await Router.replace(
      qs.stringifyUrl({
        url: pathname,
        query: {
          ...query,
          eonxCheckInId: undefined,
        },
      }),
      undefined,
      { shallow: true },
    )
  }, [query, pathname])

  /*
    Reset cookies during a check-in error
  */
  const onError = useCallback(() => {
    Cookies.set(ORIGIN_COOKIE, 'none')
    updateEonxEnvironment()
    setError(true)
  }, [])

  const eonxCheckIn = useCallback(
    async ({
      eonxCheckInId,
      venueSlug,
    }: {
      eonxCheckInId: string
      venueSlug: string
    }) => {
      setLoading(true)
      const res = await eonxCheckInGuest({
        id: eonxCheckInId,
        venueSlug,
      })
      if (!res.error) {
        await onSuccess()
        sendLog(`Eonx check in success`, { venueSlug })
      } else {
        onError()
        sendLog(`Eonx check in failure`, { venueSlug })
      }
      setLoading(false)
    },
    [eonxCheckInGuest, onSuccess, sendLog, onError],
  )

  useEffectOnce(() => {
    if (query.eonxCheckInId) {
      sendLog(`Eonx check in ID found`)
    }
  })

  useDebounce(
    () => {
      if (enableCheckIn && venueSlug && query.eonxCheckInId) {
        eonxCheckIn({ eonxCheckInId: String(query.eonxCheckInId), venueSlug })
      }
    },
    100,
    [enableCheckIn, query.eonxCheckInId, venueSlug],
  )

  // TODO display Eonx Check-in error also?: https://linear.app/mr-yum/issue/SRV-1292/spike-how-do-we-handle-eonx-check-in-failure-in-the-pass-app

  /**
   * Because we are using cookies which are outside react rendering state we need to defer rendering child components
   * until we're sure the cookies are updated
   * This is the loading skeleton for Venue Page which is the only page I can see the Pass App linking in to.
   * If it linked to another page the user would see an incorrect loading skeleton for a second.
   * We could fix that by providing loading context down and having each page handle loading state
   */
  return <>{loading ? <VenuePageLoader categoriesCount={6} /> : children}</>
})
